import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LineChart from 'src/components/streametric/lineChart/LineChart';
import { PointChartType } from '../../types/point-chart';
import { SelectedInputType } from '../../types/selected-input';
import { ChartWrapper, CustomWaterLoading } from 'src/components';
import { parseISO, subHours } from 'date-fns';
import dayjs from 'dayjs';
import { TimeOptionType } from '../../types';
import { ChartData } from 'src/components/streametric/lineChart/types';
import { Box, Dialog } from '@mui/material';
import { loaderBox } from './style';
import { SessionStorageService } from 'src/features/authentication/utils';
import { getSeries, getTimestampsBasedOnTimeBox } from './utils';
import { isOnChangeTag } from '../../utils';
import { debounce } from 'lodash';
import { getAggregationType } from './utils/getAggregationType';
import { useResponsive } from 'src/hooks/useResponsive';
import { useChartData } from '../../hooks/useChartData/useChartData';
import { useChartOptions } from '../../hooks/useChartOptions';
import { GetAllResponse } from 'src/services/sites/types';
import { useToggle } from 'src/hooks/useToggle';
import { DisplayConfig } from 'src/types/readings/readings';
import { getPredefinedOptionValues } from 'src/utilities';

const AddChartModalContent = React.lazy(
  () => import('../add-chart-modal-content/AddChartModalContent')
);

type Props = {
  pointChart: PointChartType;
  removeChart: (chartId: string) => void;
  onSelectEventHandler?: (selectedInputs: SelectedInputType[], id: string) => void;
  sites?: { value: GetAllResponse[]; loaded: boolean };
  isLocalTrends?: boolean;
  fixedSiteId?: string;
};

export const LineChartTrend = ({
  pointChart,
  removeChart,
  onSelectEventHandler,
  sites,
  isLocalTrends,
  fixedSiteId,
}: Props) => {
  const isMobile = useResponsive('down', 'md');
  const { state: isEdit, onToggle: onToggleIsEdit } = useToggle();

  const sessionStorageKey = `${pointChart.id}-timeframe`;
  const selectedBoxSessionStorageKey = `${pointChart.id}-box`;

  const [selectedBox, setSelectedBox] = useState<TimeOptionType>(() => {
    const sessionStorageSelectedBox = SessionStorageService.getRaw(selectedBoxSessionStorageKey);

    return sessionStorageSelectedBox ? (sessionStorageSelectedBox as TimeOptionType) : '1h';
  });

  const [timestamps, setTimestamps] = useState<any>(() => {
    const timestamps = {
      fromDate: dayjs(subHours(new Date(), 1)),
      toDate: dayjs(new Date()),
    };
    const sessionStorageTimestamps = SessionStorageService.getParsed(sessionStorageKey);
    const sessionStorage = {
      fromDate: dayjs(new Date(sessionStorageTimestamps.fromDate)),
      toDate: dayjs(new Date(sessionStorageTimestamps.toDate)),
    };

    if (sessionStorageTimestamps) {
      return sessionStorage;
    }
    return timestamps;
  });

  const selectedTags = pointChart?.selectedInputs;

  const selectTabletTimeOptionHandler = useCallback(
    (timeOption: TimeOptionType) => {
      setSelectedBox(timeOption);
      SessionStorageService.set(selectedBoxSessionStorageKey, timeOption);

      if (timeOption !== 'custom') {
        const timestamps = getTimestampsBasedOnTimeBox(timeOption);
        const aggType = getAggregationType(
          parseISO(timestamps.fromDate.toISOString()),
          parseISO(timestamps.toDate.toISOString()),
          timeOption
        );
        aggregationTypeRef.current = aggType;
      }
    },
    [selectedBoxSessionStorageKey]
  );

  const startDate =
    selectedBox === 'custom'
      ? new Date(timestamps.fromDate.toDate().setHours(0, 0, 0, 0)).toISOString()
      : timestamps.fromDate.toISOString();
  const endDate =
    selectedBox === 'custom'
      ? new Date(timestamps.toDate.toDate().setHours(23, 59, 59, 999)).toISOString()
      : timestamps.toDate.toISOString();

  const isOnChangeChart = useMemo(
    () => selectedTags?.some((tag) => isOnChangeTag(tag)) ?? false,
    [selectedTags]
  );

  // On-change values only hold up to the present, so both the line and the x-axis must
  // stop at "now" rather than the (possibly future) end of the selected range. Computed
  // once and fed to BOTH the series builder and the axis max, so the last data point
  // lands exactly on the axis edge. If the line's endpoint and the axis max diverge, the
  // last point falls just outside the axis; with the first point outside too, Chart.js
  // finds no in-range point, collapses the y-scale to [0, 1], and draws the flat line
  // off-canvas — a blank chart.
  const onChangeEndDate = useMemo(() => {
    if (!isOnChangeChart) return endDate;
    return new Date(Math.min(new Date(endDate).getTime(), Date.now())).toISOString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, isOnChangeChart]);

  const aggregationTypePayload = useMemo(
    () => getAggregationType(parseISO(startDate), parseISO(endDate), selectedBox),
    [startDate, endDate, selectedBox]
  );

  useEffect(() => {
    aggregationTypeRef.current = aggregationTypePayload;
  }, [aggregationTypePayload]);

  const onChangeTimestapHandler = useCallback(
    (values: [Date, Date]) => {
      setTimestamps(() => {
        const newState = { fromDate: dayjs(values[0]), toDate: dayjs(values[1]) };

        SessionStorageService.set(sessionStorageKey, JSON.stringify(newState));
        return newState;
      });
    },
    [sessionStorageKey]
  );

  const aggregationTypeRef = useRef<'two_hour' | 'five_min' | 'thirty_min' | null>(null);

  const onSelectTimestampHandler = (timeOption: TimeOptionType) => {
    setTimeout(() => {
      setTimestamps(() => {
        const newState = getTimestampsBasedOnTimeBox(timeOption);
        SessionStorageService.set(sessionStorageKey, JSON.stringify(newState));
        return newState;
      });
    }, 0);
  };

  const { data, isLoading } = useChartData({
    timestamps: timestamps,
    selectedTags: selectedTags,
    startDate: startDate,
    endDate: endDate,
    aggregationTypePayload: aggregationTypePayload,
  });

  // Responses are keyed by row index ("2") or row index + statistic variant ("2__min"),
  // so the leading segment is the index into selectedTags — i.e. which y-axis the tag owns.
  const displayByTagIndex = useMemo(() => {
    const displays: Record<number, DisplayConfig | null> = {};

    data.forEach((item: { tagId: string; display: DisplayConfig | null }) => {
      const tagIndex = Number(item.tagId.split('__')[0]);
      if (!displays[tagIndex] && item.display) displays[tagIndex] = item.display;
    });

    return displays;
  }, [data]);

  // Per tag: the values actually present, plus every value the tag has a label configured
  // for. The configured options are folded in so a discrete tag shows all of its states on
  // the axis even when the selected time frame holds no reading at some of them — the same
  // union the TrendWidget builds. Anything outside this set is a tick position no reading
  // ever had and no option was configured for, so it stays unlabelled instead of being
  // given a made-up state name.
  const dataValuesByTagIndex = useMemo(() => {
    const values: Record<number, number[]> = {};

    data.forEach((item: { tagId: string; values: Record<string, number> }) => {
      const tagIndex = Number(item.tagId.split('__')[0]);
      const readings = Object.values(item.values ?? {}).filter(
        (value) => typeof value === 'number' && !Number.isNaN(value)
      );
      values[tagIndex] = [...(values[tagIndex] ?? []), ...readings];
    });

    Object.entries(displayByTagIndex).forEach(([key, display]) => {
      const tagIndex = Number(key);
      values[tagIndex] = [...(values[tagIndex] ?? []), ...getPredefinedOptionValues(display)];
    });

    Object.keys(values).forEach((key) => {
      values[Number(key)] = [...new Set(values[Number(key)])].sort((a, b) => a - b);
    });

    return values;
  }, [data, displayByTagIndex]);

  const timestampsRef = useRef(timestamps);
  useEffect(() => {
    timestampsRef.current = timestamps;
  }, [timestamps]);

  const extendTimeframeOnZoomOutOrPan = useCallback(
    (min: string, max: string) => {
      const formatedMin = dayjs(new Date(min));
      const formatedMax = dayjs(new Date(max));
      const currentTimestamps = timestampsRef.current;

      const isOutOfCurrentScope =
        formatedMin.isBefore(currentTimestamps.fromDate) ||
        formatedMax.isAfter(currentTimestamps.toDate);
      const isDifferentFromCurrent =
        !formatedMin.isSame(currentTimestamps.fromDate) ||
        !formatedMax.isSame(currentTimestamps.toDate);

      const start = parseISO(formatedMin.toISOString());
      const end = parseISO(formatedMax.toISOString());
      const newAggregationType = getAggregationType(start, end, 'custom');
      const prevAggregationType = aggregationTypeRef.current;

      const isAggregationChanged = newAggregationType !== prevAggregationType;
      if ((isOutOfCurrentScope && isDifferentFromCurrent) || isAggregationChanged) {
        aggregationTypeRef.current = newAggregationType;
        setTimestamps({ fromDate: formatedMin, toDate: formatedMax });
        setSelectedBox('custom');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const debouncedExtendTimeframeOnZoomOutOrPan = useRef(
    debounce(extendTimeframeOnZoomOutOrPan, 800)
  ).current;

  const allSeries: ChartData[] = useMemo(
    () => getSeries(data, pointChart.selectedInputs, startDate, onChangeEndDate),
    [data, pointChart.selectedInputs, startDate, onChangeEndDate]
  );

  const extendedColors = useMemo(
    () =>
      selectedTags?.flatMap((tag) =>
        tag.statistic === 'Min-Average-Max'
          ? [tag.color ?? '', tag.color ?? '', tag.color ?? '']
          : [tag.color ?? '']
      ),
    [selectedTags]
  );

  const seriesAxisIds = useMemo(() => {
    let axisCounter = 0;
    return selectedTags.flatMap((tag) => {
      const axisId = axisCounter === 0 ? 'y' : `y${axisCounter - 1}`;
      axisCounter++;
      return tag.statistic === 'Min-Average-Max' ? [axisId, axisId, axisId] : [axisId];
    });
  }, [selectedTags]);

  // Series index -> index into selectedTags, so the tooltip can find the tag's labelling
  // config. Same flatMap shape as seriesAxisIds: a Min-Average-Max row yields three series.
  const seriesTagIndexes = useMemo(
    () =>
      selectedTags.flatMap((tag, index) =>
        tag.statistic === 'Min-Average-Max' ? [index, index, index] : [index]
      ),
    [selectedTags]
  );

  const toDateCustom = useMemo(
    () =>
      new Date(
        dayjs(timestamps.toDate)
          .set('hour', 23)
          .set('minute', 59)
          .set('second', 59)
          .set('millisecond', 0)
          .toISOString()
      ),
    [timestamps.toDate]
  );

  // For on-change, the axis max is the exact endpoint the line ends at (onChangeEndDate),
  // so the last data point always lands on the axis edge and the chart never blanks.
  const chartToDate = useMemo(() => {
    if (isOnChangeChart) return new Date(onChangeEndDate);
    return selectedBox === 'custom' ? toDateCustom : timestamps.toDate;
  }, [isOnChangeChart, onChangeEndDate, selectedBox, toDateCustom, timestamps.toDate]);

  const {
    chartRef,
    onResetZoomHandler,
    zoomInHandler,
    zoomOutHandler,
    isPanAndZoomActive,
    togglePanAndZoom,
    options,
    changeTimeFormatHandler,
  } = useChartOptions({
    fromDate: timestamps.fromDate,
    toDate: chartToDate,
    debouncedExtendTimeframeOnZoomOutOrPan: debouncedExtendTimeframeOnZoomOutOrPan,
    tags: selectedTags,
    isMobile: isMobile,
    displayByTagIndex: displayByTagIndex,
    dataValuesByTagIndex: dataValuesByTagIndex,
    seriesTagIndexes: seriesTagIndexes,
  });

  const onResetTimestamp = useCallback(() => {
    setTimestamps(() => {
      const timestamps = {
        fromDate: dayjs(subHours(new Date(), 1)),
        toDate: dayjs(new Date()),
      };
      const sessionStorageTimestamps = SessionStorageService.getParsed(sessionStorageKey);
      const sessionStorage = {
        fromDate: dayjs(new Date(sessionStorageTimestamps.fromDate)),
        toDate: dayjs(new Date(sessionStorageTimestamps.toDate)),
      };

      if (sessionStorageTimestamps) {
        return sessionStorage;
      }
      return timestamps;
    });
    setSelectedBox(() => {
      const sessionStorageSelectedBox = SessionStorageService.getRaw(selectedBoxSessionStorageKey);
      return sessionStorageSelectedBox ? (sessionStorageSelectedBox as TimeOptionType) : '1h';
    });
  }, [sessionStorageKey, selectedBoxSessionStorageKey]);

  console.log(allSeries, 'all series');

  return (
    <>
      <ChartWrapper
        zoomOutHandler={zoomOutHandler}
        zoomInHandler={zoomInHandler}
        onResetZoomHandler={onResetZoomHandler}
        onResetTimestamp={onResetTimestamp}
        isPanAndZoomActive={isPanAndZoomActive}
        togglePanAndZoom={togglePanAndZoom}
        removeChart={removeChart}
        id={pointChart.id}
        timestamps={timestamps}
        onChangeTimestapHandler={onChangeTimestapHandler}
        onSelectTimestampHandler={onSelectTimestampHandler}
        selectTabletTimeOptionHandler={selectTabletTimeOptionHandler}
        setTimestamps={setTimestamps}
        sessionStorageKey={sessionStorageKey}
        selectedBoxSessionStorageKey={selectedBoxSessionStorageKey}
        sites={sites}
        onSelectEventHandler={onSelectEventHandler}
        pointChartData={allSeries}
        selectedBox={selectedBox}
        setSelectedBox={setSelectedBox}
        changeTimeFormatHandler={changeTimeFormatHandler}
        fixedSiteId={fixedSiteId}
        selectedInputs={isLocalTrends ? pointChart.selectedInputs : undefined}
        onEditOpen={onToggleIsEdit}
      >
        {isLoading && allSeries.length === 0 ? (
          <Box sx={loaderBox}>
            <CustomWaterLoading />
          </Box>
        ) : null}

        <LineChart
          chartRef={chartRef}
          options={options}
          allSeries={allSeries}
          extendedColors={extendedColors}
          seriesAxisIds={seriesAxisIds}
          isLoading={isLoading && allSeries.length > 0}
        />
      </ChartWrapper>
      <Dialog
        open={isEdit}
        onClose={onToggleIsEdit}
        sx={{
          '& .MuiPaper-root': {
            width: '1200px',
            overflowX: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <Suspense fallback={<CustomWaterLoading />}>
          <AddChartModalContent
            onToggleHandler={onToggleIsEdit}
            onSelectEventHandler={onSelectEventHandler}
            isParticularChart
            chartId={pointChart.id}
            selectedInputs={pointChart.selectedInputs}
            sites={sites}
            fixedSiteId={fixedSiteId}
          />
        </Suspense>
      </Dialog>
    </>
  );
};
export default LineChartTrend;
